import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { DaysOfMonthConstants } from '../../../../../common/days-of-month.constants';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ContinuationMode, DayOfWeek, Frequency, Month, SchedulerDefinition, YearlyCalculationMode, YearlySchedulerDefinition } from '../../../../api/scheduler-service';
import { SchedulerOptionsComponent } from '../scheduler-options-component';
import { YearlyCalculationModeDisplayMap } from '../../../../model/schedule/yearly-mode-display';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'yearly-schedule-options',
  imports: [CommonModule, BrowserModule, BrowserAnimationsModule, MatCardModule, MatSlideToggleModule, MatButtonToggleModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatChipsModule],
  templateUrl: './yearly-schedule-options.component.html',
  styleUrl: './yearly-schedule-options.component.css'
})
export class YearlyScheduleOptionsComponent implements OnInit, SchedulerOptionsComponent{
  @Input() valueChange : Function | undefined;

  ngOnInit(): void {
    this.yearlyOptionSelectedDayOfTheWeek.valueChanges.subscribe(val => {
      if (val == null) {
        // If user attempts to deselect the only chip, revert to last valid selection.
        this.yearlyOptionSelectedDayOfTheWeek.setValue(this.lastSelectedDayOfWeek, { emitEvent: false });
      } else {
        this.lastSelectedDayOfWeek = val;
      }
    });

    setTimeout(() => this.yearlyOptionsForm.valueChanges.subscribe(() => {
      if (this.valueChange != undefined) {
        this.valueChange();
      }
    }), 0);
  }

  daysOfMonth = DaysOfMonthConstants.daysOfMonth;
  weekdays = Object.values(DayOfWeek);
  months = Object.values(Month);
  continuationModes = Object.values(ContinuationMode);
  
  lastSelectedDayOfWeek : string = DayOfWeek.Monday;

  modes = Object.values(YearlyCalculationMode);
  modesDisplayMap = YearlyCalculationModeDisplayMap;


  interval : FormControl = new FormControl(1, Validators.min(1));
  yearlyOptionDayMode : FormControl = new FormControl(YearlyCalculationMode.OnSelectedDay);
  continuationMode: FormControl = new FormControl(ContinuationMode.Strict);
  yearlyOptionsSelectedMonth : FormControl = new FormControl(Month.January);
  yearlyOptionsSelectedDaysOfMonth : FormControl = new FormControl(this.daysOfMonth[0]);
  yearlyOptionSelectedDayOfTheWeek: FormControl = new FormControl(DayOfWeek.Monday, []);
  specificDay: FormControl = new FormControl(1, [Validators.min(1), Validators.max(31)]);
  dependency: FormControl = new FormControl(false);

  yearlyOptionsForm: FormGroup = new FormGroup({
    "interval" : this.interval,
    "dayMode" : this.yearlyOptionDayMode,
    "specificDay" : this.specificDay,
    "specificDays" : this.yearlyOptionsSelectedDaysOfMonth,
    "selectedMonths" : this.yearlyOptionsSelectedMonth,
    "selectedDayOfTheWeek" : this.yearlyOptionSelectedDayOfTheWeek,
    "continuationMode" : this.continuationMode,
    "dependency" : this.dependency
  });

  isYearlyOnSelectedDayOfWeek() : boolean {
    return this.isYearlyOnFirstDayOfWeek() || 
          this.isYearlyOnSecondDayOfWeek() ||  
          this.isYearlyOnThridDayOfWeek() ||  
          this.isYearlyOnFourthDayOfWeek() ||  
          this.isYearlyOnLastDayOfWeek();
  }


  isYearlyOnSpecificDays() : boolean {
    return this.yearlyOptionDayMode.value === YearlyCalculationMode.OnSelectedDay;
  }
  
  isYearlyOnFirstDayOfWeek() : boolean {
    return this.yearlyOptionDayMode.value === YearlyCalculationMode.OnFirstSelectedWeekdayOfMonth;
  }
  
  isYearlyOnLastDayOfWeek() : boolean {
    return this.yearlyOptionDayMode.value === YearlyCalculationMode.OnLastSelectedWeekdayOfMonth;
  }

  isYearlyOnSecondDayOfWeek() : boolean {
    return this.yearlyOptionDayMode.value === YearlyCalculationMode.OnSecondSelectedWeekdayOfMonth;
  }

  isYearlyOnThridDayOfWeek() : boolean {
    return this.yearlyOptionDayMode.value === YearlyCalculationMode.OnThirdSelectedWeekdayOfMonth;
  }

  isYearlyOnFourthDayOfWeek() : boolean {
    return this.yearlyOptionDayMode.value === YearlyCalculationMode.OnFourthSelectedWeekdayOfMonth;
  }


  getSchedulerDefinition() : SchedulerDefinition {
    const schedulerDefinition: YearlySchedulerDefinition = {
      frequency: Frequency.Yearly,
      interval: this.interval.value,
      month: this.yearlyOptionsSelectedMonth.value,
      mode: this.yearlyOptionDayMode.value,
      continuationMode: this.continuationMode.value,
      selectedDayOfMonth: this.daysOfMonth.indexOf(this.yearlyOptionsSelectedDaysOfMonth.value) + 1,
      selectedDayOfWeek: this.yearlyOptionSelectedDayOfTheWeek.value,
      eventDependency: this.dependency.value,
    };
    return schedulerDefinition;
  }

  getIntervalSuffix() : string {
    return (this.interval.value > 1) ? "years" : "year";
  }

  intervalHasError() : boolean {
    return !this.interval.valid;
  }

  intervalErrorMessage() : string {
    if (this.interval.hasError("min")) {
      return "Interval must be 1 or higher";
    }

    return "";
  }
}
